import { ActionIcon, Group, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useIsJiraIntegrated } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import CollapsableStack from '@repo/common/components/CollapsableStack';
import { EntityType } from '@repo/common/enums/entityType';
import { Icon, IconButton } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { IGlossaryTerm, IQuestion, ISecodaEntity } from '../../api';
import {
	useAuthUser,
	useIntegrationPrefetched,
	useSuspenseFeatureAccess,
} from '../../api';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../api/hooks/user/useExtendedUserList';
import { trackDashboardSubscribed } from '../../features/BiDashboard/utils/tracking';
import { useRightSidebarPortalBehavior } from '../../hooks/useRightSidebarPortalBehavior';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import { useCheckEntityUpdatePermission } from '../../utils/authorization/roles';
import { useFeatureFlags } from '../../utils/featureFlags';
import { SelectableProperty, StaticProperty } from './EntityPropertySidebar';
import type { SidebarEntityKeys } from './types';
import { getOwnerAndGroupSelectorOptions } from './utils';

import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { Link } from 'react-router';
import { CustomPropertiesController } from '../../features/CustomProperties';
import { AssigneePropertySelector } from '../Properties/AssigneePropertySelector';
import { CollectionParentPropertySelector } from '../Properties/CollectionParentPropertySelector';
import { CollectionsPropertySelector } from '../Properties/CollectionsPropertySelector';
import { GovernancePropertySelector } from '../Properties/GovernancePropertySelector';
import { OwnersPropertySelector } from '../Properties/OwnersPropertySelector';
import { PriorityPropertySelector } from '../Properties/PriorityPropertySelector';
import { PropertySelector } from '../Properties/PropertySelector';
import { QuestionStatusPropertySelector } from '../Properties/QuestionStatusPropertySelector';
import { StatusPropertySelector } from '../Properties/StatusPropertySelector';
import TagPropertySelector from '../Properties/TagPropertySelector';
import { TeamsPropertySelector } from '../Properties/TeamsPropertySelector';
import { VerificationPropertySelector } from '../Properties/VerificationPropertySelector';
import { FreshnessStack } from './SidesheetStacks/Freshness/FreshnessStack';
import { JiraIssueStack } from './SidesheetStacks/Jira/JiraIssueStack';
import MetadataStack from './SidesheetStacks/Metadata/MetadataStack';
import { RelatedResourceStack } from './SidesheetStacks/RelatedResource/RelatedResourceStack';
import { SynonymsStack } from './SidesheetStacks/Synonyms/SynonymsStack';

export interface EntitySidebarContentProps {
	entity: ISecodaEntity | IQuestion;
	updateEntity: (
		key: SidebarEntityKeys,
		value: DjangoValueType,
		saveRemotely?: boolean
	) => void;
	withFrequentUsers?: boolean;
	withVerifiedSelector?: boolean;
	withGovernanceSelector?: boolean;
	withCollectionSelector?: boolean;
	withTagSelector?: boolean;
	withOwnerSelector?: boolean;
	withRelatedResourceSelector?: boolean;
	withSynonymsSelector?: boolean;
	withCustomPropertyEditors?: boolean;
	withEntityPopularity?: boolean;
	withEntityRowCount?: boolean;
	withEntityByteSize?: boolean;
	withTeamSelector?: boolean;
	// Question-specific properties:
	withAssignedToSelector?: boolean;
	withPrioritySelector?: boolean;
	withStatusSelector?: boolean;
	withSource?: string;
	withMetricSection?: JSX.Element;
	withCustomPublishElement?: JSX.Element;
	withCollectionParentSelector?: boolean;
}

const EntitySidebarContent = observer(
	({
		entity,
		updateEntity,
		withFrequentUsers = false,
		withVerifiedSelector = false,
		withGovernanceSelector = false,
		withCollectionSelector = false,
		withTagSelector = false,
		withOwnerSelector = false,
		withCustomPublishElement,
		withRelatedResourceSelector = false,
		withSynonymsSelector = false,
		withCustomPropertyEditors = false,
		withEntityPopularity = false,
		withEntityRowCount = false,
		withEntityByteSize = false,
		withTeamSelector = true,
		// Question-specific properties:
		withAssignedToSelector = false,
		withPrioritySelector = false,
		withStatusSelector = false,
		withSource = undefined,
		withMetricSection,
		withCollectionParentSelector = false,
	}: EntitySidebarContentProps) => {
		const { user, workspace, isGuestUser, isAdminUser } = useAuthUser();
		const { teamsPermissionsV2Frontend } = useFeatureFlags();
		const editorOfEntity = useCheckEntityUpdatePermission({ entity });
		const viewerOfEntity = !editorOfEntity;
		const { secodaCoreAccess, collectionsAccess } = useSuspenseFeatureAccess();
		const { dataQualityScoreSla } = useFeatureFlags();

		// Use mobile overlay mode hook for portal calculation
		const shouldUsePortal = useRightSidebarPortalBehavior();

		const { data: integration } = useIntegrationPrefetched({
			id: entity.integration,
			options: {
				enabled:
					Boolean(entity.integration) &&
					entity.entity_type !== EntityType.metric,
			},
		});

		const forceSourceOwners = !!integration?.force_source_owners;

		const canShowSLA =
			dataQualityScoreSla && entity?.entity_type?.toLowerCase() === 'table';

		const canLinkJira = entity?.entity_type === EntityType.question;
		const { data: isJiraIntegrated } = useIsJiraIntegrated({});

		const { usersVisibleToGuests } = useGuestUserList();
		const { activeUsers, userGroups: groups } = useExtendedUserList();

		const [localOverrides, setLocalOverrides] = useState<
			Partial<ISecodaEntity | IQuestion>
		>({});
		const previousUpdatedAtRef = useRef(entity.updated_at);
		const previousEntityRef = useRef(entity);

		if (previousUpdatedAtRef.current !== entity.updated_at) {
			setLocalOverrides({});
			previousUpdatedAtRef.current = entity.updated_at;
			previousEntityRef.current = entity;
		} else if (previousEntityRef.current !== entity) {
			setLocalOverrides({});
			previousEntityRef.current = entity;
		}

		const handleEntityChange = useCallback(
			(key: SidebarEntityKeys) =>
				(value: DjangoValueType, saveRemotely = true) => {
					if (!saveRemotely) {
						setLocalOverrides((prev) => ({ ...prev, [key]: value }));
					} else {
						setLocalOverrides((prev) => ({ ...prev, [key]: value }));
						updateEntity(key, value, saveRemotely);
					}
					trackEvent(
						`${entity.entity_type}/${key}/update`,
						{ id: entity.id },
						user,
						workspace
					);

					if (
						entity.entity_type === 'bi_dashboard' &&
						(key === 'subscribers' || key === 'subscribers_groups')
					) {
						trackDashboardSubscribed(entity.id, user, workspace);
					}

					if (key === 'teams' && teamsPermissionsV2Frontend) {
						showNotification({
							message: 'Team changes may take a few minutes to propagate.',
							color: 'blue',
						});
					}
				},
			[
				updateEntity,
				entity.entity_type,
				entity.id,
				user,
				workspace,
				teamsPermissionsV2Frontend,
			]
		);

		const entityView = useMemo<ISecodaEntity | IQuestion>(
			() => ({
				...entity,
				...localOverrides,
			}),
			[entity, localOverrides]
		);

		const subscriberOptions = useMemo(
			() =>
				getOwnerAndGroupSelectorOptions(
					(isGuestUser ? usersVisibleToGuests : activeUsers) ?? [],
					groups ?? [],
					handleEntityChange('subscribers'),
					handleEntityChange('subscribers_groups')
				).map((owner) => ({
					...owner,
					itemTypeName: 'subscribers',
				})),
			[
				isGuestUser,
				usersVisibleToGuests,
				activeUsers,
				groups,
				handleEntityChange,
			]
		);

		const withAddCustomProperty = withCustomPropertyEditors && editorOfEntity;

		const subscribers = useMemo(
			() => [
				...(entityView.subscribers ?? []),
				...(entityView.subscribers_groups ?? []),
			],
			[entityView]
		);

		const validateSubscriberSelection = useCallback(
			(item: any, currentSelection: any[]) => {
				// Only apply validation for viewers
				if (user.role !== 'viewer') {
					return true;
				}

				// Allow viewers to add/remove themselves
				if (item.value === user.id) {
					return true;
				}

				// Show notification if viewer tries to add someone else
				showNotification({
					title: 'Cannot add subscriber',
					message: 'Users with the role Viewer can only subscribe themselves.',
					color: 'red',
				});

				return false;
			},
			[user.role, user.id]
		);

		return (
			<Stack>
				{withMetricSection && (
					<CollapsableStack groupName="Configuration">
						{withMetricSection}
					</CollapsableStack>
				)}
				<CollapsableStack
					groupName="Properties"
					actions={
						withAddCustomProperty && (
							<Link to={'/settings/properties'}>
								<IconButton
									data-testid="add-custom-property-032487fh"
									iconName="plus"
									variant="tertiary"
									tooltip="Add property"
								/>
							</Link>
						)
					}
				>
					<Stack spacing="xs">
						{withAssignedToSelector && (
							<SelectableProperty label="Assigned to" dataTestId="assigned_to">
								<AssigneePropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									handleEntityChange={handleEntityChange}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{withPrioritySelector && (
							<SelectableProperty label="Priority" dataTestId="priority">
								<PriorityPropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									onChange={handleEntityChange('priority')}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{withStatusSelector && (
							<SelectableProperty label="Status">
								<QuestionStatusPropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									onChange={handleEntityChange('status')}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{editorOfEntity &&
							!withCustomPublishElement &&
							!withStatusSelector && (
								<SelectableProperty label="Status" dataTestId="published">
									<StatusPropertySelector
										entity={entity}
										viewerOfEntity={viewerOfEntity}
										onChange={handleEntityChange('published')}
										position="left-start"
										withinPortal={shouldUsePortal}
									/>
								</SelectableProperty>
							)}
						{!!withCustomPublishElement && withCustomPublishElement}
						{withTagSelector && (
							<SelectableProperty label="Tags" dataTestId="tags">
								<TagPropertySelector
									noMaxHeightDropdown
									initialValue={entity.tags}
									readOnly={viewerOfEntity}
									placeholder={viewerOfEntity ? 'No tags' : 'Add tag'}
									placeholderIconName="tag"
									forceVariant="tertiary"
									forceSourceTags={!!integration?.force_source_tags}
									entityIntegrationId={integration?.id}
									onChange={handleEntityChange('tags')}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{withCollectionSelector && collectionsAccess && (
							<SelectableProperty label="Collections" dataTestId="collections">
								<CollectionsPropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									onChange={handleEntityChange('collections')}
									position="left-start"
									withinPortal={shouldUsePortal}
									noMaxHeightDropdown
								/>
							</SelectableProperty>
						)}
						{forceSourceOwners ? (
							<RichTooltip
								title={`No owners were set in ${integration.name}`}
								body={'To maintain in Secoda, go to integration settings.'}
								linkLabel={
									isAdminUser ? `${integration?.name} settings` : undefined
								}
								linkUrl={
									isAdminUser
										? `/integrations/${integration?.id}/preferences`
										: undefined
								}
								position="left-start"
							>
								{withOwnerSelector && (
									<SelectableProperty label="Owners" dataTestId="owners">
										<OwnersPropertySelector
											entity={entityView}
											viewerOfEntity={viewerOfEntity}
											integration={integration}
											handleEntityChange={handleEntityChange}
											noMaxHeightDropdown
										/>
									</SelectableProperty>
								)}
							</RichTooltip>
						) : (
							withOwnerSelector && (
								<SelectableProperty label="Owners" dataTestId="owners">
									<OwnersPropertySelector
										entity={entityView}
										viewerOfEntity={viewerOfEntity}
										integration={integration}
										handleEntityChange={handleEntityChange}
										position="left-start"
										withinPortal={shouldUsePortal}
										noMaxHeightDropdown
									/>
								</SelectableProperty>
							)
						)}
						{withGovernanceSelector && secodaCoreAccess && (
							<SelectableProperty label="Governance" dataTestId="governance">
								<GovernancePropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									onChange={handleEntityChange('pii')}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{withVerifiedSelector && (
							<SelectableProperty
								label="Verification"
								dataTestId="verification"
							>
								<VerificationPropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									onChange={handleEntityChange('verified')}
									position="left-start"
									withinPortal={shouldUsePortal}
								/>
							</SelectableProperty>
						)}
						{withTeamSelector && !isGuestUser && (
							<SelectableProperty label="Teams" dataTestId="teams">
								<TeamsPropertySelector
									entity={entity}
									viewerOfEntity={viewerOfEntity}
									integration={integration}
									onChange={handleEntityChange('teams')}
									position="left-start"
									withinPortal={shouldUsePortal}
									noMaxHeightDropdown
								/>
							</SelectableProperty>
						)}
						{withCollectionParentSelector && (
							<Group>
								<SelectableProperty label="Parent" dataTestId="collections">
									<CollectionParentPropertySelector
										entity={entity}
										viewerOfEntity={viewerOfEntity}
										onChange={handleEntityChange('collections')}
										position="left-start"
										withinPortal={shouldUsePortal}
									/>
								</SelectableProperty>
								{entity.collections?.length > 0 && (
									<ActionIcon
										size="xs"
										onClick={() => handleEntityChange('collections')([])}
									>
										<Icon name="x" />
									</ActionIcon>
								)}
							</Group>
						)}
						{withCustomPropertyEditors && (
							<CustomPropertiesController
								maxWidth={165}
								entity={entity}
								position="left-start"
								withinPortal={shouldUsePortal}
							/>
						)}
					</Stack>
				</CollapsableStack>
				{canLinkJira && isJiraIntegrated && (
					<JiraIssueStack
						question={entity as IQuestion}
						isViewer={viewerOfEntity}
					/>
				)}
				{canShowSLA && <FreshnessStack entity={entity} />}
				{withSynonymsSelector && (
					<SynonymsStack entity={entity as IGlossaryTerm} />
				)}
				<RelatedResourceStack
					entity={entity}
					isVisible={withRelatedResourceSelector}
					withinPortal={shouldUsePortal}
				/>
				<MetadataStack
					integration={integration}
					entity={entity}
					visibleUsers={
						(isGuestUser ? usersVisibleToGuests : activeUsers) ?? []
					}
					withFrequentUsers={withFrequentUsers}
					withEntityPopularity={withEntityPopularity}
					withEntityRowCount={withEntityRowCount}
					withEntityByteSize={withEntityByteSize}
				/>
				<CollapsableStack groupName="Subscribers">
					<Stack spacing="xs">
						<SelectableProperty label="Subscribers" dataTestId="subscribers">
							<PropertySelector
								selected={subscribers}
								type="multi"
								value="subscribers"
								iconType="avatar"
								options={subscriberOptions}
								permittedId={user.id}
								isViewerUser={false}
								readOnly={false}
								onValidateSelection={validateSubscriberSelection}
								position={shouldUsePortal ? 'left-start' : 'top-start'} // renders to the right in overlay mode, presumably due to not having enough space in the viewport?
								withinPortal={shouldUsePortal}
							/>
						</SelectableProperty>
						{withSource && (
							<StaticProperty type="source" label="Source" value={withSource} />
						)}
					</Stack>
				</CollapsableStack>
			</Stack>
		);
	}
);

export default EntitySidebarContent;
